import { LOGIN_TYPE } from '@/utils/const'
import {
  ArrowLeftOutlined,
  LockOutlined,
  MailOutlined,
  MobileOutlined,
  SafetyOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Button, Checkbox, Form, Input, Select, SelectProps, Space } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { areaCodeLists } from '@/utils/const'
import useMessage from '@/hooks/useMessage'
import { useNavigate } from 'react-router-dom'
import usePersistDataStore from '@/stores/persistData'
import { doRegister, doRegisterEnterprise, getVerifyCode, getVerifyCodeByPhone } from '@/api/login'

type LabelRender = SelectProps['labelRender']

const RegForm: React.FC<{ registrantType: string; onClickBack: () => void }> = ({ registrantType, onClickBack }) => {
  const { t } = useTranslation()
  const { message } = useMessage()
  const navigate = useNavigate()
  const lang = usePersistDataStore(state => state.lang)
  const [form] = Form.useForm()
  const initialValues = {
    account: '',
    verifyCode: '',
    password: '',
    name: '',
    areaCode: '+61',
    abn: '',
    agreed: '',
  }
  const [type, setType] = useState(LOGIN_TYPE.EMAIL)

  const [codeTimer, setCodeTimer] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (codeTimer === 60) {
      intervalRef.current = setInterval(() => {
        setCodeTimer(pre => pre - 1)
      }, 1000)
    } else if (codeTimer === 0) {
      clearInterval(intervalRef.current as NodeJS.Timeout)
    }
  }, [codeTimer])

  const [areaCodeOptions, setAreaCodeOptions] = useState<{ label: string; value: string; key: string | number }[]>([])
  useEffect(() => {
    setAreaCodeOptions(
      areaCodeLists.map((item, index) => ({
        label: (lang === 'en_US' ? item.en : item.cn) + ' ' + item.value,
        value: item.value,
        key: index,
      }))
    )
  }, [lang])
  const labelRender: LabelRender = props => {
    return <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{props.value}</span>
  }

  const verify_pwd = useRef('')

  const doGetCode = async () => {
    const account = form.getFieldValue('account')
    if (!account && account === '') {
      message.error(type === LOGIN_TYPE.PHONE ? t('user.enterPhone') : t('user.enterEmail'))
      return
    } else {
      let res: any
      if (type === LOGIN_TYPE.PHONE) {
        const phone_code = form.getFieldValue('areaCode')
        try {
          res = await getVerifyCodeByPhone({ phone_code, phone: account, type: 'reg' })
        } catch (error: any) {
          console.log(error)
          message.error(error.message || error.msg || error)
          return
        }
        if (res.code === 1001) {
          message.success(t('user.sendCodeSuccess'))
          verify_pwd.current = res.data.code
          setCodeTimer(60)
        } else {
          message.error(res.msg)
          return
        }
      } else if (type === LOGIN_TYPE.EMAIL) {
        try {
          res = await getVerifyCode({ account, get_type: 'register' })
        } catch (error: any) {
          console.log(error)
          message.error(error.message || error.msg || error)
          return
        }
        if (res.code === 1001) {
          message.success(t('user.sendCodeSuccess'))
          verify_pwd.current = res.data.code
          setCodeTimer(60)
        } else {
          message.error(res.msg)
          return
        }
      }
    }
  }

  const handleRegister = async () => {
    let values, res: any
    try {
      values = await form.validateFields()
    } catch (error) {
      console.log(error)
      return
    }
    const params: { [key: string]: string | number | undefined } = {
      name: values.name,
      account: values.account,
      password: values.password,
      verify_code: values.verifyCode,
      verify_pwd: verify_pwd.current,
    }
    if (type === LOGIN_TYPE.PHONE) {
      params.phone_code = values.areaCode
    }
    if (registrantType === 'personal') {
      try {
        res = await doRegister(params)
      } catch (error: any) {
        console.log(error)
        message.error(error.message || error.msg || error)
        return
      }
      if (res.code === 1001) {
        message.success(t('user.registerSuccess'))
        navigate('/login')
      } else {
        message.error(t('user.registerFail'))
        return
      }
    } else {
      params.company_code = values.abn
      try {
        res = await doRegisterEnterprise(params)
      } catch (error: any) {
        console.log(error)
        message.error(error.message || error.msg || error)
        return
      }
      if (res.code === 1001) {
        message.success(t('user.registerSuccess'))
        navigate('/login')
      } else {
        message.error(t('user.registerFail'))
        return
      }
    }
  }

  const validateABN = (_: any, value: string) => {
    if (registrantType === 'personal') {
      return Promise.resolve()
    } else {
      if (value === undefined || value === '') {
        return Promise.reject(t('user.enterABN'))
      } else {
        return Promise.resolve()
      }
    }
  }

  const validateEmailFormat = (_: any, value: string) => {
    if (type === LOGIN_TYPE.EMAIL) {
      const reg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9._%+-]+(\.[a-zA-Z0-9_-]+)+$/
      if (!reg.test(value)) {
        return Promise.reject(t('user.emailFormatError'))
      } else {
        return Promise.resolve()
      }
    } else {
      return Promise.resolve()
    }
  }

  const validateAgreement = (_: any, value: boolean) => {
    if (value) {
      return Promise.resolve()
    } else {
      return Promise.reject(t('common.haveToAgree'))
    }
  }

  return (
    <div className='reg-form-page'>
      <div className='reg-form-header'>
        <div className='back-btn' onClick={onClickBack}>
          <ArrowLeftOutlined />
          <span>{t('common.back')}</span>
        </div>
        <div className='header-separator'></div>
        <div className='reg-form-header-text'>{t('user.regFormTip')}</div>
      </div>
      <div className='reg-form'>
        <Form form={form} size='large' layout='vertical' initialValues={initialValues}>
          <Form.Item label={type === LOGIN_TYPE.EMAIL ? t('user.email') : t('user.phoneNumber')} required>
            {/* {registrantType === 'personal' && (
              <div
                className='extra-btn'
                onClick={() => {
                  if (type === LOGIN_TYPE.EMAIL) {
                    setType(LOGIN_TYPE.PHONE)
                  } else {
                    setType(LOGIN_TYPE.EMAIL)
                  }
                }}>
                {type === LOGIN_TYPE.EMAIL ? t('user.switchPhoneReg') : t('user.switchEmailReg')}
              </div>
            )} */}
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name='areaCode' noStyle hidden={type === LOGIN_TYPE.EMAIL}>
                <Select
                  style={{ width: '100px' }}
                  options={areaCodeOptions}
                  labelRender={labelRender}
                  popupMatchSelectWidth={200}
                />
              </Form.Item>
              <Form.Item
                noStyle
                name='account'
                rules={[
                  {
                    required: true,
                    message: type === LOGIN_TYPE.PHONE ? t('user.enterPhone') : t('user.enterEmail'),
                  },
                  { validator: validateEmailFormat },
                ]}>
                <Input
                  style={{ width: type === LOGIN_TYPE.EMAIL ? '100%' : 'calc(100% - 100px)' }}
                  placeholder={type === LOGIN_TYPE.EMAIL ? t('user.enterEmail') : t('user.enterPhone')}
                  prefix={type === LOGIN_TYPE.EMAIL ? <MailOutlined /> : <MobileOutlined />}
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item label={t('user.verificationCode')} required>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name='verifyCode'
                noStyle
                rules={[{ required: true, message: t('user.enterVerificationCode') }]}>
                <Input
                  style={{ width: 'calc(100% - 90px)' }}
                  placeholder={t('user.enterVerificationCode')}
                  prefix={<SafetyOutlined />}
                />
              </Form.Item>
              <Button onClick={doGetCode} disabled={codeTimer !== 0} style={{ width: '90px' }}>
                {codeTimer === 0 ? t('user.getCode') : codeTimer}
              </Button>
            </Space.Compact>
          </Form.Item>
          <Form.Item
            name='password'
            label={t('user.password')}
            rules={[{ required: true, message: t('user.enterPassword') }]}>
            <Input.Password prefix={<LockOutlined />} placeholder={t('user.enterPassword')} />
          </Form.Item>
          <Form.Item
            name='name'
            label={registrantType === 'personal' ? t('user.fullName') : t('user.businessName')}
            rules={[
              {
                required: true,
                message: registrantType === 'personal' ? t('user.enterFullName') : t('user.enterBusinessName'),
              },
            ]}>
            <Input
              prefix={<UserOutlined />}
              placeholder={registrantType === 'personal' ? t('user.enterFullName') : t('user.enterBusinessName')}
            />
          </Form.Item>
          <Form.Item
            name='abn'
            label={t('user.ABN')}
            rules={[{ validator: validateABN }]}
            hidden={registrantType === 'personal'}
            required>
            <Input prefix={<UserOutlined />} placeholder={t('user.enterABN')} />
          </Form.Item>
          <Form.Item name='agreed' valuePropName='checked' label={null} rules={[{ validator: validateAgreement }]}>
            <Checkbox>
              <span className='agreementText'>{t('user.iAgree')}</span>
              <span
                className='agreementBtn'
                onClick={() => {
                  console.log('agreement')
                }}>
                {t('common.privacyPolicy')}
              </span>
              <span className='agreementText'>{t('common.and')}</span>
              <span
                className='agreementBtn'
                onClick={() => {
                  console.log('term')
                }}>
                {t('common.termOfUse')}
              </span>
            </Checkbox>
          </Form.Item>
        </Form>
        <div className='form-btn'>
          <Button style={{ width: '100%', borderRadius: '50px' }} type='primary' size='large' onClick={handleRegister}>
            {t('user.register')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default RegForm
